/* eslint-disable no-unreachable */
import {
  AutocompleteInput,
  ConfigurationContext,
  FormInput,
  HasuraQuery,
  NotificationsContext,
} from '@kirz/mui-admin';
import dayjs from 'dayjs';
import React, { useCallback, useContext } from 'react';

import { DealStageDataForm } from 'components/DealStageDataForm';
import { FunnelStageType } from 'constants/funnels';
import { Permissions } from 'constants/permissions';
import { UserContext } from 'contexts';
import { SettingsContext } from 'contexts/SettingsContext';
import { DealBookingInputs } from 'pages/Deal/tabs/PriceTab/PriceTab';

export function useDealStageManager(strict: boolean = true) {
  const { user, hasPermission } = useContext(UserContext);
  const { hasura } = useContext(ConfigurationContext);
  const { dealCancelReasons, nonTargetedLeadType } =
    useContext(SettingsContext);
  const { showAlert, showPrompt } = useContext(NotificationsContext);

  const validateDealStageChange = useCallback(
    async (
      dealId: number,
      sourceStageType: string,
      destinationStageType: string,
    ) => {
      if (sourceStageType === destinationStageType) {
        return { updates: [] };
      }

      if (
        ([FunnelStageType.FAILURE, FunnelStageType.WRONG].includes(
          sourceStageType,
        ) &&
          !hasPermission(Permissions.DEALS_CANCEL_FINAL_STAGE)) ||
        (sourceStageType === FunnelStageType.DEAL &&
          !hasPermission(Permissions.DEALS_CANCEL_FINAL_STAGE))
      ) {
        showAlert('Сделка находится на конечном этапе', 'error');
        return false;
      }

      if (
        [FunnelStageType.DIFFICULT_CALL_STAGE].includes(destinationStageType)
      ) {
        showAlert('На данный этап сделки попадают автоматически', 'error');
        return false;
      }

      let note = null;
      const updates: HasuraQuery[] = [];

      if (
        destinationStageType === FunnelStageType.DEAL ||
        destinationStageType === FunnelStageType.BOOKING
      ) {
        const isDeal = destinationStageType === FunnelStageType.DEAL;

        const fields = [
          'sellerId',
          'clientId',
          'estateId',
          'userId',
          'obligations',
          'paymentForm',
          'dealPrice',
          'dealContractPrice',
          'contractForm',
          ...(isDeal ? ['dealDate', 'contractNumber'] : []),
        ];

        const [deal] = await hasura.request({
          source: 'deal',
          where: { id: { _eq: dealId } },
          limit: 1,
          selection: fields,
        });

        let defaultValues = fields.reduce(
          (acc, key) => ({ ...acc, [key]: deal[key] ?? null }),
          {} as Record<string, any>,
        );

        try {
          // re-prompt until at least one of «Клиент»/«Покупатель» is filled
          // eslint-disable-next-line no-constant-condition
          while (true) {
            const result = await showPrompt({
              title: isDeal ? 'Успех' : 'Бронирование',
              form: <DealStageDataForm variant={isDeal ? 'deal' : 'booking'} />,
              accept: 'Подтвердить',
              cancel: 'Отмена',
              width: 600,
              formProps: { defaultValues },
            });

            if (!result) {
              throw new Error('cancel');
            }

            if (!result.sellerId && !result.clientId) {
              showAlert('Укажите клиента или покупателя', 'error');
              defaultValues = { ...defaultValues, ...result };
              continue;
            }

            updates.push({
              type: 'mutation',
              action: 'update',
              source: 'deal',
              where: { id: { _eq: dealId } },
              set: fields.reduce(
                (acc, key) => ({ ...acc, [key]: result[key] ?? null }),
                {} as Record<string, any>,
              ),
            });

            break;
          }
        } catch {
          showAlert('Перенос отменен');
          return false;
        }
      }

      if (
        [FunnelStageType.FAILURE, FunnelStageType.WRONG].includes(
          destinationStageType,
        )
      ) {
        try {
          const result = await showPrompt({
            title: 'Требуется подтверждение',
            form: (
              <AutocompleteInput
                name="reason"
                label="Причина отказа"
                options={(destinationStageType === FunnelStageType.FAILURE
                  ? dealCancelReasons
                  : nonTargetedLeadType
                ).map((x) => ({ text: x, value: x }))}
                required
                inputProps={{ autoFocus: true, autoComplete: 'none' }}
              />
            ),
            accept: 'Подтвердить',
            cancel: 'Отмена',
          });

          if (!result) {
            throw new Error('cancel');
          }

          note = `Причина отказа: ${result.reason}`;
        } catch {
          showAlert('Перенос отменен');
          return false;
        }
      } else if ([FunnelStageType.BOOKING].includes(destinationStageType)) {
        try {
          const [existingDeal] = await hasura.request({
            type: 'query',
            source: 'deal',
            where: {
              id: { _eq: dealId },
            },
            selection: `
              prepaymentDate
              prepaymentSum
              prepaymentComment
              prepaymentFileId
              isPrepaymentDone
            `,
          });

          if (
            !existingDeal.prepaymentDate ||
            !existingDeal.prepaymentSum ||
            !existingDeal.isPrepaymentDone
          ) {
            const result = await showPrompt({
              title: 'Внесенный аванс',
              form: <>{DealBookingInputs}</>,
              accept: 'Подтвердить',
              cancel: 'Отмена',
            });

            if (!result) {
              throw new Error('cancel');
            }

            updates.push({
              type: 'mutation',
              action: 'update',
              source: 'deal',
              where: {
                id: { _eq: dealId },
              },
              set: {
                ...result,
                isPrepaymentDone: true,
              },
            });
          }
        } catch {
          showAlert('Перенос отменен');
          return false;
        }
      } else if (strict && destinationStageType !== FunnelStageType.DEAL) {
        try {
          const result = await showPrompt({
            title: 'Требуется подтверждение',
            form: (
              <>
                <FormInput
                  multiline
                  rows={5}
                  required
                  name="comment"
                  label="Комментарий"
                />
              </>
            ),
            accept: 'Подтвердить',
            cancel: 'Отмена',
          });

          if (!result) {
            throw new Error('cancel');
          }

          note = `${result.comment}${
            result.dealDate
              ? `. Дата сделки: ${dayjs(result.dealDate).format('DD.MM.YYYY')}`
              : ''
          }`;
        } catch {
          showAlert('Перенос отменен');
          return false;
        }
      }

      if (note) {
        updates.push({
          type: 'mutation',
          action: 'insert',
          source: 'dealNote',
          items: [
            {
              dealId,
              text: note,
              userId: user.id,
              companyId: user.companyId,
            },
          ],
        });
      }

      return {
        updates: [
          ...(sourceStageType === FunnelStageType.DEAL
            ? [
                {
                  type: 'mutation',
                  action: 'update',
                  source: 'deal',
                  where: {
                    id: { _eq: dealId },
                  },
                  set: {
                    fixedPrices: null,
                    fixedSalaries: null,
                  },
                } as HasuraQuery,
              ]
            : []),
          ...updates,
        ],
      };
    },
    [user, dealCancelReasons, nonTargetedLeadType, showAlert, showPrompt],
  );

  return {
    validateDealStageChange,
  };
}
